// src/api/rptClient.js
//
// Reporting API client — calls the TB middle-tier RPT proxy
// (HR-TB-RPT-PROXY -> Hinge central router -> HINGE-RPT-API).
//
// All calls ride tbGetJson (TeamBridge COOKIE SESSION: credentials:"include",
// OIDC -> /api/login -> session cookie, automatic re-auth on expiry) so auth
// behavior matches every other TB API call through teambridge.*.directv.com.
// Do NOT switch to proxyGetJson: it sends an Entra JWT with credentials:"omit"
// (the TB gateway 401s: "invalid or expired token") and historically stamped
// Content-Type: application/json on bodyless GETs, which the TB Mule DataWeave
// blind-parses as JSON and 500s on ("Unable to parse empty input").

import { tbGetJson } from "./client";

/* ---- env read (CRA + Vite dual, same pattern as client.js) ---- */
function readEnv(key, fallback = "") {
    // try {
    //     // Vite
    //     // eslint-disable-next-line no-undef
    //     if (typeof import.meta !== "undefined" && import.meta?.env && import.meta.env[`VITE_${key}`] != null) {
    //         // eslint-disable-next-line no-undef
    //         return String(import.meta.env[`VITE_${key}`]);
    //     }
    // } catch { /* not vite */ }
    // try {
    //     if (typeof process !== "undefined" && process?.env && process.env[`REACT_APP_${key}`] != null) {
    //         return String(process.env[`REACT_APP_${key}`]);
    //     }
    // } catch { /* not CRA */ }
    
    try {
        // CRA inlines only STATIC process.env.REACT_APP_* references, not dynamic keys.
        const staticMap = {
            RPT_BASE: process.env.REACT_APP_RPT_BASE,
            TB_PROXY_BASE: process.env.REACT_APP_TB_PROXY_BASE,
        };
        if (staticMap[key] != null) {
            return String(staticMap[key]);
        }
    } catch { /* not CRA */ }
    return fallback;
}

function resolveRptBase() {
    const explicit = readEnv("RPT_BASE", "").trim().replace(/\/+$/, "");
   // alert(`RPT_BASE=${explicit || "(not set)"} (from env)`);
    if (explicit) return explicit;
    const tbBase = readEnv("TB_PROXY_BASE", "").trim().replace(/\/+$/, "");
    if (tbBase) return `${tbBase}/api/v1/rpt`;
    return "https://teambridge.directv.com/dtvcs-teambridge-api/api/v1/rpt";
}

const RPT_BASE = resolveRptBase();

/* ---- query-string builder (drops empty values) ---- */
function qs(params = {}) {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        const s = String(v).trim();
        if (s === "") return;
        usp.set(k, s);
    });
    const out = usp.toString();
    return out ? `?${out}` : "";
}

function get(path, params) {
    // Cookie-session GET: credentials:"include", no Content-Type header,
    // session re-auth handled by tbFetchWithReauth inside tbGetJson.
    return tbGetJson(`${RPT_BASE}/${path}${qs(params)}`);
}

/* =========================
   Endpoints
   =========================
   Shared filter params accepted by aggregate/list endpoints:
   { source, type, status, vendor, partner, org, cohort, from, to, q }
*/

export function rptFilters() {
    return get("filters");
}

export function rptSummary(filters = {}) {
    return get("summary", filters);
}

export function rptFunnel(filters = {}) {
    return get("funnel", filters);
}

/** Partner scorecard: nested partner -> location tree (with per-location
 *  org_unit_code as `orgCode`). Honours the shared filter params. */
export function rptPartners(filters = {}) {
    return get("partners", filters);
}

/** by: vendor | org_unit | cohort | source | request_type | cp_profile | partner | location | target_failures */
export function rptBreakdown(by, filters = {}) {
    return get("breakdown", { ...filters, by });
}

export function rptTrend(days, filters = {}) {
    return get("trend", { ...filters, days });
}

/** extra: { limit, offset, sort, dir } */
export function rptRequests(filters = {}, extra = {}) {
    return get("requests", { ...filters, ...extra });
}

export function rptRequestDetail(source, key) {
    return get(`requests/${encodeURIComponent(source)}/${encodeURIComponent(key)}`);
}

/** extra: { target, limit, offset, sort, dir } */
export function rptFailures(filters = {}, extra = {}) {
    return get("failures", { ...filters, ...extra });
}

export function rptSla(filters = {}) {
    return get("sla", filters);
}

/** App provisioning coverage from ServiceNow work notes.
 *  No args: per-app bucket counts. { app: CODE }: worker list for that app.
 *  Optional worker filters: { vendor, cohort, q }. */
export function rptAppCoverage(params = {}) {
    return get("appcoverage", params);
}

export { RPT_BASE };